import React from 'react';

import DoneIcon from '@mui/icons-material/Done';
import {
  IconButton, Stack, Tooltip,
} from '@mui/material';
import dayjs from 'dayjs';
import _ from 'lodash';

import Button from '../../../../components/Button';
import DataGrid, { DataGridProps } from '../../../../components/DataGrid';
import { useAcceptExamMarkingRequestMutation, useDropExamMarkingRequestMutation } from '../../../../services/exams';
import { ExamMarkingRequest } from '../../../../services/exams/types';
import { ExamRequestStatus } from '../../../../types/common';
import useError from '../../../../utils/react/hooks/useError';
import { DueDateText } from '../../index.styles';

interface RequestsListProps extends Omit<DataGridProps, 'columns'> {
  allowDrop?: boolean;
  displayMarker?: boolean,
  rows: ExamMarkingRequest[];
}

const RequestsList = ({
  allowDrop = false, displayMarker = false, rows, ...rest
} : RequestsListProps) => {
  const [acceptRequest, {
    isError: isAcceptRequestError,
    error: acceptRequestError,
  }] = useAcceptExamMarkingRequestMutation();

  const [dropRequest, {
    isError: isDropRequestError,
    error: dropRequestError,
  }] = useDropExamMarkingRequestMutation();

  useError(isAcceptRequestError, acceptRequestError);
  useError(isDropRequestError, dropRequestError);

  return (
    <DataGrid
      initialState={{
        sorting: {
          sortModel: [{ field: 'dueDatetime', sort: 'asc' }],
        },
        pagination: {
          paginationModel: { pageSize: 10 },
        },
      }}
      columns={[
        {
          field: 'options',
          headerName: 'Options',
          width: 120,
          renderCell: ({ row }: any) => (
            <Stack direction="row" spacing={1}>
              <IconButton
                size="small"
                color="success"
                onClick={async (e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  await acceptRequest({
                    requestId: row.requestId,
                  });
                }}
              >
                <DoneIcon />
              </IconButton>
              {(allowDrop) && (
                <Tooltip
                  title="Awaiting acceptance from other markers. Until then, the paper is still your responsibility!"
                  disableHoverListener={
                    row.status === ExamRequestStatus.PendingAcceptance
                  }
                >
                  <div>
                    <Button
                      color="error"
                      size="small"
                      variant="text"
                      disabled={row.status !== ExamRequestStatus.PendingAcceptance}
                      onClick={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        await dropRequest({
                          requestId: row.requestId,
                        });
                      }}
                    >
                      Drop
                    </Button>
                  </div>
                </Tooltip>
              )}
            </Stack>
          ),
        },
        {
          field: 'dueDatetime',
          headerName: 'Due',
          width: 120,
          renderCell: ({ row } : any) => (
            <DueDateText $overdue={row?.dueDatetime && row.dueDatetime < dayjs().valueOf()}>
              {row.dueDatetime ? _.upperFirst(dayjs(row.dueDatetime).fromNow()) : '-'}
            </DueDateText>
          ),
          sortComparator: (v1: number, v2: number) => {
            const current = dayjs().valueOf();
            return ((v1 ?? Infinity) - current) - ((v2 ?? Infinity) - current);
          },
        },
        {
          field: 'studentName',
          headerName: 'Student',
          width: 140,
        },
        {
          field: 'displayedExamLabel',
          headerName: 'Exam',
          width: 300,
        },
        ...(displayMarker ? [{
          field: 'markerName',
          headerName: 'Marker',
          width: 160,
        }] : []),
      ]}
      getRowId={(e) => e.requestId}
      pageSizeOptions={[10, 50]}
      rows={rows}
      {...rest}
    />
  );
};

export default RequestsList;
